import { Button } from "@/components/ui/shadcn/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/shadcn/dropdown-menu";
import {
  CommandWorkspaceLayout,
  WorkspaceNavItem,
} from "./layouts/CommandWorkspaceLayout";
import { Home, Users, LogOut } from "lucide-react";
import { Outlet } from "react-router";
import { useCallback, useMemo } from "react";
import { useAuth } from "@/components/contexts/AuthContext/UseAuth";

const adminNavLinks: WorkspaceNavItem[] = [
  {
    label: "Dashboard",
    path: "/",
    Icon: Home,
  },
  {
    label: "Leads",
    path: "/leads",
    Icon: Users,
  },
];

const buildProfileFooter = (
  name: string,
  email: string | undefined,
  onSignOut: () => void,
) => {
  return (collapsed: boolean) => {
    const initials = name
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase();

    if (collapsed) {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Account menu for ${name}`}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground transition hover:bg-primary/90"
            >
              {initials}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-56"
            side="top"
            align="start"
            alignOffset={0}
            sideOffset={12}
          >
            <div className="px-3 py-2">
              <p className="text-sm font-semibold text-gray-900">{name}</p>
              <p className="text-xs text-gray-500">{email}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onSignOut}
              className="gap-2 text-sm text-destructive focus:text-destructive"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    return (
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-lg font-semibold text-primary">
          {initials}
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-gray-900">{name}</span>
          <span className="text-xs text-gray-500">{email}</span>
          <div className="mt-2 flex items-center gap-3">
            <Button
              variant="link"
              className="px-0 text-xs font-medium text-destructive"
              onClick={onSignOut}
            >
              Sign out
            </Button>
          </div>
        </div>
      </div>
    );
  };
};

export function Layout() {
  const { logout, user } = useAuth();
  const profile = user?.profile;
  const name =
    profile?.name ||
    [profile?.given_name, profile?.family_name].filter(Boolean).join(" ") ||
    profile?.preferred_username ||
    profile?.email ||
    "Administrator";

  const handleLogout = useCallback(async () => {
    await logout();
  }, [logout]);

  const navFooter = useMemo(
    () => buildProfileFooter(name, profile?.email, handleLogout),
    [handleLogout, name, profile?.email],
  );

  return (
    <CommandWorkspaceLayout
      navItems={adminNavLinks}
      navFooter={navFooter}
      title="Admin workspace"
    >
      <Outlet />
    </CommandWorkspaceLayout>
  );
}
